import React, { FC, useState, useEffect, PropsWithChildren, useContext } from 'react';
import classNames from 'classnames';
import View from '../view';
import Icon from '../icon';
import Group, { GroupContext } from './Group';

interface IRadioProps {
  className?: string;
  defaultChecked?: boolean;
  checked?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: () => void;
}

const BasicRadio: FC<PropsWithChildren<Omit<IRadioProps, 'value'>>> = ({ children, className, defaultChecked = false, disabled, checked: propsChecked, onChange }) => {
  const [checked, setChecked] = useState<boolean>(typeof propsChecked !== 'undefined' ? propsChecked : defaultChecked);
  const rootCls = classNames(
    'ink-radio',
    {
      'ink-radio--disabled': disabled,
      'ink-radio--selected': checked,
    },
    className,
  );
  const contentCls = classNames('ink-radio__label');

  const onClick = () => {
    if (disabled) {
      return;
    }

    // 已选中，不可取消
    if (checked) {
      return;
    }
    /**
     * props.checked 传入，为受控组件，通过 传入 checked 来更新本地 checked
     * props.checked 未传入，未非受控组件，使用内部状态更新
     */
    if (typeof propsChecked === 'undefined') {
      setChecked(true);
    }

    if (onChange) {
      onChange();
    }
  };

  useEffect(() => {
    if (typeof propsChecked !== 'undefined') {
      setChecked(propsChecked);
    }
  }, [propsChecked]);

  return (
    <View className={rootCls} onClick={onClick}>
      <View className="ink-radio__icon-cnt">{checked && <Icon name="check" className="ink-radio__icon" />}</View>
      <View className={contentCls}>{children}</View>
    </View>
  );
};

const Radio: FC<PropsWithChildren<Omit<IRadioProps, 'onChange'>>> & { Group: typeof Group } = ({ value, ...restProps }) => {
  const groupContext = useContext(GroupContext);

  const radioProps: IRadioProps = { ...restProps };

  if (groupContext) {
    const { value: groupValue, toggleOption, disabled: groupDisabled } = groupContext;
    const isChecked = !!(groupValue && value === groupValue);
    radioProps.checked = isChecked;
    if (typeof restProps.disabled === 'undefined') {
      radioProps.disabled = groupDisabled;
    }
    radioProps.onChange = () => {
      if (toggleOption) {
        toggleOption({ value: value! });
      }
    };
  }

  return <BasicRadio {...radioProps} />;
};

Radio.Group = Group;

export default Radio;
